<template>
    <div class="autocomplete-input">
        <input
            :autocompleteOld="'force_off_' + Math.random() * 10000 + 1"
            autocomplete="off"
            class="form-control" :class="{ 'form-error': isError, 'form-change': isChanged }" :name="name"
            v-model="keyword"
            @input="onInput($event.target.value)"
            @keyup.esc="showList = false"
            @blur="select($event);showList = false"
            @focus="focus()"
            @keydown.down="moveDown" @keydown.up="moveUp"
            @keydown.enter="select($event)"
            @keydown.tab="select($event)"
            :placeholder="placeholder"
            :required="required"
            :readonly="readonly"
        />
        <input :autocompleteOld="'force_off_' + Math.random() * 10000 + 1" autocomplete="off" class="hidden" :name="name + '_id'" v-model="keywordId" />
        <ul v-show="showList" class="options-list" ref="list">
            <li v-for="(option, index) in optionMatches" :key="index" :class="{'highlighted': index === highlightedPosition}" ref="list-items"
              z-index="950"
              @mouseleave="highlightedPosition = -1"
              @mouseenter="highlightedPosition = index"
              @mousedown="select">
                <slot name="item" :code="option.code" :itemname="option.name" :item="option"></slot>
            </li>
        </ul>
    </div>
</template>
<script>
import Axios from '../classes/Axios.js';
import 'regenerator-runtime/runtime';
import {defineComponent} from 'vue';
export default defineComponent ({
    emits: ['select', 'error', 'message'],
    data() {
        return {
            isChanged: false,
            isDefaultShow: false, // to trigger computed optionMatches()
            selected: false,
            showList: false,
            isError: false,
            keyword: '',
            keywordId: '',
            debouncedInput: '',
            highlightedPosition: this.highlight, // Current list item to highlight
            mustMatch: this.match, // allows for not forcing a selection
            query: '', // Search sent to Rest Resource API
            resources: [],
            loading: false,
            oasisApi: new Axios('/restv2'),
            showError: false, // avoid duplicated message show when user type 10 chars
        }
    },

    props: {
        displayChanges: {
            required: false,
            type: Boolean,
            default: false
        },
        defaultShow: {
            required: false,
            type: Boolean,
            default: false
        },
        preselect: {
            type: [ String, Number, Object ],
            required: false,
            default: ''
        },
        display: {
            required: false,
            type: String,
            default: 'name'
        },
        secondaryDisplay: {
            required: false,
            type: String,
            default: undefined
        },
        name: {
            type: String,
            required: true
        },

        // Choose to pass in the list of options or an endpoint to search
        options: {
            type: Array,
            required: false,
        },
        endpoint: {
            required: false,
            type: String,
        },

        highlight: {
            type: Number,
            required: false,
            default: 0
        },
        match: {
            type: Boolean,
            required: false,
            default: true,
        },
        placeholder: {
            type: String,
            required: false
        },
        required: {
            type: Boolean,
            required: false,
            default: false
        },
        readonly: {
            type: Boolean,
            required: false,
            default: false
        },
        searchProps: {
            type: Array,
            required: false,
            default: () => [ 'name', 'code' ]
        },

        // Primary filter to be used with resourceClass, e.g. ?filter=
        primaryFilter: {
            required: false,
            type: String,
            default: undefined
        },

        // Filter options that are not the primary filter.  Helps with limiting options, e.g. { type__in: 'POSTED', 'CLOSED' }
        filterOptions: {
            required: false,
            type: Object,
            default () {
                return {}
            }
        },

        // Number of characters to collect before querying API
        minKeywordLength: {
            required: false,
            type: [ Number, String ],
            default: 1,
        },

        sortOptionMatches: {
            required: false,
            type: Boolean,
            default: true
        }
    },

    computed: {
        optionMatches() {
            // Will return matches that start with desired values and append loose matches after.
            // Will also prefer to match names than codes
            // console.log(this.searchProps);
            if (! this.keyword && this.isDefaultShow && this.options) return this.options; // show all
            else if (! this.keyword && this.minKeywordLength != 0) return [];
            if (! this.keyword && this.loading) return [];
            else if (! this.keyword) this.keyword = '';
            else if (typeof this.keyword === 'number') this.keyword = String(this.keyword);
            const bestMatch = new RegExp('^' + this.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');
            const looseMatch = new RegExp('(?!^)' + this.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'i');

            if (this.endpoint && this.resources && this.resources.length) {
                let bestMatches = this.resources.filter(option => {
                    for (let i in this.searchProps) {
                        let prop = this.searchProps[i];
                        if (option.hasOwnProperty(prop) && option[prop] && option[prop].match(bestMatch)) return (option);
                    }
                })
                if (this.sortOptionMatches) {
                    bestMatches = bestMatches.sort(this.optionCompByCode);
                }

                let looseMatches = this.resources.filter(option => {
                    for (let i in this.searchProps) {
                        let prop = this.searchProps[i];
                        if (option.hasOwnProperty(prop) && option[prop] && option[prop].match(looseMatch)) return (option);
                    }
                })
                if (this.sortOptionMatches) {
                    looseMatches = looseMatches.sort(this.optionCompByCode);
                }

                let matches = bestMatches.concat(looseMatches);

                return matches.filter((match, index, self) => self.findIndex(t => t.id === match.id) === index);
            } else if (this.endpoint) {
                // not supposed to be here
                // console.log(this.endpoint, 'no resources');
                return [];
            } else {
                let bestMatches = this.options.filter(option => {
                    for (let i in this.searchProps) {
                        let prop = this.searchProps[i];
                        if (option.hasOwnProperty(prop) && option[prop] && option[prop].match(bestMatch)) return (option);
                    }
                })
                if (this.sortOptionMatches) {
                    bestMatches = bestMatches.sort(this.optionCompByName);
                }

                let looseMatches = this.options.filter(option => {
                    for (let i in this.searchProps) {
                        let prop = this.searchProps[i];
                        if (option.hasOwnProperty(prop) && option[prop] && option[prop].match(looseMatch)) return (option);
                    }
                })
                if (this.sortOptionMatches) {
                    looseMatches = looseMatches.sort(this.optionCompByName);
                }

                let matches = bestMatches.concat(looseMatches);

                // only GL code will be sorted by this
                if (this.display === 'code') { 
                    matches.sort((a,b) => {
                    return a.code.localeCompare(b.code, undefined, {
                        numeric: true,
                        sensitivity: 'base',
                    })});
                }

                // console.log('matches', matches, matches.filter((match, index, self) => self.findIndex(t => t.id === match.id) === index));
                return matches.filter((match, index, self) => self.findIndex(t => t.id === match.id) === index);
            }
        }
    },

    watch: {
        // If using an async lookup, need to load as a watcher.
        keyword(newValue, oldValue) {
            if (!this.endpoint) return;
            if (newValue && newValue === this.preselect || newValue === oldValue) return false; // Forced display of input, no lookup
            if (((newValue && newValue.length) || (newValue == '' && this.minKeywordLength == 0)) && newValue !== oldValue && newValue.length >= this.minKeywordLength && !this.showError) {
                this.lookup();
            } else if (oldValue && oldValue.length && oldValue.length > this.minKeywordLength && !this.showError) {
                this.lookup(oldValue);
                // console.log(newValue, oldValue)
            }
        },
        preselect: function (newValue, oldValue) {
            // console.log('old value:', oldValue);
            // console.log('new value:', newValue);
            if (newValue && newValue.hasOwnProperty('id')) {
                // this.$emit('select', newValue);
                this.select(newValue);
                this.keywordId = newValue.id;
                if (newValue[this.display]) {
                    this.keyword = newValue[this.display];
                } else {
                    this.keyword = newValue.name;
                }
                // console.log (this.options);
            } else {
                this.keyword = newValue;
            }
        }

    },

    methods: {
        focus() {
            // If we get focus, show all.
            if (!this.keyword && this.defaultShow == true) {
                this.showList = true;
            }
            this.highlightedPosition = this.highlight;

            if (!this.keyword && this.minKeywordLength == 0) {
                this.lookup();
            }
        },
        onInput(value) {
            this.showList = value ? true : false;
            // whne user remove all values, show all
            if (this.isDefaultShow && this.showList == false) {
                this.showList = true;
            }
            this.highlightedPosition = this.highlight;
        },

        // Move the highlighed item index up
        moveUp() {
        // Only scroll down if not past the last item on the list
        if (this.showList && this.optionMatches.length && this.highlightedPosition > 0) {
            this.highlightedPosition--;

        // Else scroll to the bottom
        } else if (this.showList && this.highlightedPosition <= 0) {
            this.highlightedPosition = this.optionMatches.length - 1;
        } else {
            return false;
        }

        const element = 'list-items';
        this.$refs[element][this.highlightedPosition].scrollIntoView({block: 'nearest'});
        return true;
        },

        // Move the highlighed item index down
        moveDown() {
        // Only scroll down if not past the last item on the list
        if (this.showList && this.optionMatches.length && this.highlightedPosition < this.optionMatches.length - 1) {
            this.highlightedPosition++;

        // Else scroll to the top
        } else if (this.showList) {
            this.highlightedPosition = 0;
        } else {
            return false;
        }

        const element = 'list-items';
        this.$refs[element][this.highlightedPosition].scrollIntoView({block: 'nearest'});
        return true;
        },

        scroll(position, adjustment) {
            if (!adjustment) adjustment = 0;

            const ul = this.$refs['list'],
                li = this.$refs['list-items'];
            let scrollTop = $(ul).scrollTop(),
                viewport = scrollTop + $(ul).height(),
                elHeight = parseInt($(li).height()) + adjustment,
                elOffset = elHeight * this.highlightedPosition;

            // console.log('viewport', viewport, ' elOffset', elOffset, 'scrollTop', scrollTop, ' elHeight', elHeight);
            if (elOffset < scrollTop || (elOffset + elHeight) > viewport) $(ul).scrollTop(elOffset);
        },

        select(item, option) {
            if (!item) {
                // console.log('no item');
                return;
            } else if (item.target && item.target.value === '' && !this.isDefaultShow) {
                // should consider user remove value
                if (this.isError) this.isError = false;
                // console.log('no value', item);
                return;
            } else if (!this.showList) {
                // console.log('no list');
                return;
            }

            let value = item.target.value;
            let displayedValue;
            let selectedResource;
            let selectedOption;
            // Use resources instead of passing in an array to match
            if (this.endpoint) {
                // console.log(item, this.optionMatches, this.highlightedPosition, this.optionMatches[this.highlightedPosition])
                if (item instanceof MouseEvent && option !== undefined) {
                    selectedResource = this.optionMatches.find((match) => match.id === option.id)
                } else {
                    selectedResource = this.optionMatches[this.highlightedPosition]
                }
                // let { resource } = option

                if (!item || !item.target) {
                    return
                }

                let choice
                if (selectedResource !== undefined) {
                    choice = selectedResource
                } else if (this.highlightedPosition) {
                    choice = this.optionMatches[this.highlightedPosition]
                } else {
                    this.isError=true;
                    // should not be here
                    // console.log('no choice????');
                    return;
                }

                // Make sure it's a valid entry with a displayable property
                 if (choice && this.resources) {
                    this.selected = true
                    this.$emit('select', this.resources.find((res) => res.id === choice.id))
                    this.debouncedInput = choice[this.display]

                    if (this.secondaryDisplay) {
                        // console.log(choice)
                        this.debouncedInput += `: ${
                            choice[this.secondaryDisplay]
                        }`
                    }
                    displayedValue = this.debouncedInput 
                } else if (choice && choice.hasOwnProperty(this.display)) {
                    this.selected = true
                    this.$emit('select', choice)
                    this.debouncedInput = choice[this.display]
                    if (this.secondaryDisplay) {
                        this.debouncedInput += `: ${
                            choice[this.secondaryDisplay]
                        }`
                    }
                    displayedValue = this.debouncedInput 
                } else if (this.selected) {
                    // Do nothing if already selected and tabbing through.
                } else {
                    this.isError = true
                    this.$emit('error', choice)
                }

                this.showList = false

                // The following block is to account for someone tabbing through inputs with default values
                if (!choice && item.key === 'Tab' && this.forceSelectId) {
                    this.isError = false
                    return true
                }
                // Vue.nextTick().then(() => {
                    this.showList = false;
                    // console.log(displayedValue, this.debouncedInput, choice);
                    // console.log(this.display, choice, choice[this.display]);
                    this.keyword = displayedValue;
                    this.isError = false;
                // })
            } else {
                // Does our selection match an option value?  Error out unless we set mustMatch to false
                selectedOption = this.optionMatches[this.highlightedPosition];
                if (selectedOption === undefined) {
                    if (this.mustMatch === "false" || this.mustMatch === false || (this.isDefaultShow && value == '') ) {
                        displayedValue = value;
                        selectedOption = value;
                        this.isError = false;
                    } else {
                        // isDefaultShow value is used by only GL coding and we want to allow to set value when we type something without highlighted value. so just use this value to distinguish btw GL and others.
                        if (this.isDefaultShow) {
                            if (this.optionMatches.length > 0) {
                                selectedOption = this.optionMatches[0];
                                if (this.display === 'code') displayedValue = selectedOption.code;
                                else displayedValue = selectedOption.name;
                                this.isError = false;
                            } else {
                                displayedValue = value;
                                this.isError = true;
                                return false;
                            }
                        } else {
                            displayedValue = value;
                            this.isError = true;
                            return false;
                        }
                    }

                // Match a custom option or code, name, ID
                } else if (this.display && selectedOption.hasOwnProperty(this.display)) {
                    displayedValue = selectedOption[this.display];
                    this.isError = false;
                } else if (selectedOption.code !== undefined) {
                    displayedValue = selectedOption.code;
                    this.isError = false;
                } else if (selectedOption.name !== undefined) {
                    displayedValue = selectedOption.name;
                    this.isError = false;
                } else if (selectedOption.id !== undefined) {
                    displayedValue = selectedOption.id;
                    this.isError = false;
                } else {
                    displayedValue = value;
                    this.isError = true;
                    return false;
                }

                if (selectedOption && selectedOption.id !== undefined) {
                    // console.log('keywordId: ', selectedOption.id, selectedOption)
                    this.keywordId = selectedOption.id;
                }

                this.showList = false;
                this.highlightedPosition = this.highlight;
                this.keyword = displayedValue;
                // console.log('Selected:');
                // console.log(selectedOption);
                this.$emit('select', selectedOption);
                if (!this.preselect && this.isDefaultShow && typeof(selectedOption) === 'object' && this.displayChanges) {
                    // add one specific case to addClass 'form-change' to can be updated
                    this.isChanged = true;
                } else if (this.displayChanges && !this.preselect && !this.keyword) {
                    // do nothing.
                } else if (this.displayChanges && this.preselect !== this.keyword) {
                    this.isChanged = true;
                } else if (this.displayChanges) {
                    // console.log('B', this.preselect, this.keyword, selectedOption);
                }
                return true;
            }

        },
        reset () {
            this.selected = false
            this.showList = false
            this.isError = false
            this.keyword = ''
            this.keywordId = ''
            this.highlightedPosition = 0
            this.resources = []
        },
        // search name first, result will be shown by sorting name first
        optionCompByName (a, b) {
            let aFixed, bFixed;

            if (a.name && b.name) {
                aFixed = a.name.toLowerCase();
                bFixed = b.name.toLowerCase();
            } else if (a.code && b.code) {
                aFixed = a.code.toLowerCase();
                bFixed = b.code.toLowerCase();
            } else {
                return a - b;
            }

            if (aFixed > bFixed) return 1;
            else if (aFixed < bFixed) return -1;
            else return 0
        },
        // search code first, result will be shown by sorting code first
        optionCompByCode (a, b) {
            let aFixed, bFixed;

            if (a.code && b.code) {
                aFixed = a.code.toLowerCase();
                bFixed = b.code.toLowerCase();
            } else if (a.name && b.name) {
                aFixed = a.name.toLowerCase();
                bFixed = b.name.toLowerCase();
            } else {
                return a - b;
            }

            if (aFixed > bFixed) return 1;
            else if (aFixed < bFixed) return -1;
            else return 0
        },

        // Rest resource lookup
        async lookup (oldValue) {
            // console.log('lookup');
            if (!this.showList || !this.endpoint) return;
            let searchTerm
            if (oldValue) {
                searchTerm = oldValue
            } else {
                searchTerm = this.keyword; //this.debouncedInput
            }
            this.loading = true
            if (searchTerm !== undefined) {
                let query = Object.assign({}, this.filterOptions)
                query.search = searchTerm
                try {
                    var queryString = Object.keys(query).map(key => key + '=' + query[key]).join('&');
                    const url = this.endpoint + '?' + queryString;

                    let response = await this.oasisApi.get(url);
                    // console.log(url, response);
                    if (response.status == 200 || response.status == 201) {
                        this.resources = response.data.results;
                    } else {
                        this.showError = true;
                        let errorMessage = { "title": "Warning", "body": 'Please contact support team.', "buttonNo": false, "buttonYesLabel": "OK"  };
                        this.$emit('error', errorMessage)
                    }      
                } catch (error) {
                    this.$emit('message', error)
                }
            } else {
                // console.log('no keyword')
            }

            this.loading = false
            return this.resources;
        },

        // Rest resource get detail
        async detailById (id) {
            let ResourceClass = this.resourceClass

            if (id) {
                try {
                    let result = await ResourceClass.detail(id)
                    if (result) this.keyword = result.attributes[this.displayProp]
                    if (result) this.debouncedInput = result.attributes[this.displayProp]
                    if (result && this.secondaryDisplay) {
                        this.keyword += `: ${result.attributes[this.secondaryDisplay]}`
                        this.debouncedInput += `: ${
                        result.attributes[this.secondaryDisplay]
                        }`
                    }
                    this.$emit('select', result)
                    return result
                } catch (error) {
                    this.$emit('message', error)
                }
            } else {
                return null
            }
        },

        // Rest resource get detail
        async detailByCode (code) {
            let ResourceClass = this.resourceClass

            if (code) {
                try {
                    let result = await ResourceClass.filter({
                        query: { code__eq: code }
                    })
                    if (result) this.keyword = result[0].attributes[this.displayProp]
                    if (result && this.secondaryDisplay) {
                        this.keyword += `: ${
                        result[0].attributes[this.secondaryDisplay]
                        }`
                    }
                    this.$emit('select', result[0])
                    return result[0]
                } catch (error) {
                    this.$emit('message', error)
                }
            } else {
                return null
            }
        },

        clear () {
            this.debouncedInput = undefined
        }
    },

    created() {
        if (!this.options && !this.endpoint) {
            return null;
        }
    },

    mounted() {
        if (!this.keyword && this.preselect) this.keyword = typeof this.preselect === 'object' ? (this.preselect.name || this.preselect.value || '') : this.preselect || '';
        if (this.defaultShow) this.isDefaultShow = true;
    }
});
</script>
<style scoped>
    .options-list {
        min-width: 150px;
        max-width: 500px;
        max-height: 1000%;
        overflow: scroll;
    }
</style>